/**
 * CatalogPage — merchant /catalogs (sidebar, detail, product rows, dialogs).
 * Consolidated from CatalogList, CatalogDetail, CatalogItemCard.
 */

import React, { useReducer, useCallback, useEffect, useRef, useState, memo } from 'react';
import {
  Box, Typography, Button, Paper, useTheme, useMediaQuery,
  TextField, InputAdornment, IconButton, Skeleton, Snackbar, Alert,
  CircularProgress, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Select, MenuItem, FormControl,
} from '@mui/material';
import {
  AddPhotoAlternate, Add, ImageOutlined, Delete,
  CollectionsBookmark, ArrowBack, PlayCircleOutline,
  FamilyRestroom, Close
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

import DynamicMerchantTabs from '../Layout/DynamicMerchantTabs';
import { useMerchantPermissions } from '../../../contexts/MerchantPermissionContext';
import SecureImage from '../../Common/SecureImage';
import merchantAPI, { catalogAPI } from '../../../services/merchantAPI';
import FlutterServicesAPI from '../../../services/flutterServices';
import { getBaseUrl } from '../../../utils/environmentUtils';
import {
  catalogColors, catalogFonts, buildAddToCatalogState,
  formatProductLabel, getCatalogItemListLines, getCatalogThumbSrc, getCatalogPreviewSrc,
  CATALOG_NAME_MAX_LENGTH,
} from './catalogTheme';
import { CreateEditCatalogDialog, EditCaptionDialog, ShareLinkDialog } from './CatalogDialogs';
import './catlog.css';

// Custom icons matching shared designs
const EditIconCustom = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none" {...props}>
    <path d="M21.7167 17.5L22.5 18.2833L14.9333 25.8333H14.1667V25.0667L21.7167 17.5ZM24.7167 12.5C24.5083 12.5 24.2917 12.5833 24.1333 12.7417L22.6083 14.2667L25.7333 17.3917L27.2583 15.8667C27.5833 15.5417 27.5833 15 27.2583 14.6917L25.3083 12.7417C25.1417 12.575 24.9333 12.5 24.7167 12.5ZM21.7167 15.1583L12.5 24.375V27.5H15.625L24.8417 18.2833L21.7167 15.1583Z" fill="currentColor" />
  </svg>
);

const SearchIconCustom = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="38" height="38" viewBox="0 0 38 38" fill="none" {...props}>
    <path d="M26.6 28L20.3 21.7C19.8 22.1 19.225 22.4167 18.575 22.65C17.925 22.8833 17.2333 23 16.5 23C14.6833 23 13.1458 22.3708 11.8875 21.1125C10.6292 19.8542 10 18.3167 10 16.5C10 14.6833 10.6292 13.1458 11.8875 11.8875C13.1458 10.6292 14.6833 10 16.5 10C18.3167 10 19.8542 10.6292 21.1125 11.8875C22.3708 13.1458 23 14.6833 23 16.5C23 17.2333 22.8833 17.925 22.65 18.575C22.4167 19.225 22.1 19.8 21.7 20.3L28 26.6L26.6 28ZM16.5 21C17.75 21 18.8125 20.5625 19.6875 19.6875C20.5625 18.8125 21 17.75 21 16.5C21 15.25 20.5625 14.1875 19.6875 13.3125C18.8125 12.4375 17.75 12 16.5 12C15.25 12 14.1875 12.4375 13.3125 13.3125C12.4375 14.1875 12 15.25 12 16.5C12 17.75 12.4375 18.8125 13.3125 19.6875C14.1875 20.5625 15.25 21 16.5 21Z" fill="currentColor" />
  </svg>
);

const ShareIconCustom = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="38" height="40" viewBox="0 0 38 40" fill="none" {...props}>
    <path d="M25 30C24.1667 30 23.4583 29.7083 22.875 29.125C22.2917 28.5417 22 27.8333 22 27C22 26.9 22.025 26.6667 22.075 26.3L15.05 22.2C14.7833 22.45 14.475 22.6458 14.125 22.7875C13.775 22.9292 13.4 23 13 23C12.1667 23 11.4583 22.7083 10.875 22.125C10.2917 21.5417 10 20.8333 10 20C10 19.1667 10.2917 18.4583 10.875 17.875C11.4583 17.2917 12.1667 17 13 17C13.4 17 13.775 17.0708 14.125 17.2125C14.475 17.3542 14.7833 17.55 15.05 17.8L22.075 13.7C22.0417 13.5833 22.0208 13.4708 22.0125 13.3625C22.0042 13.2542 22 13.1333 22 13C22 12.1667 22.2917 11.4583 22.875 10.875C23.4583 10.2917 24.1667 10 25 10C25.8333 10 26.5417 10.2917 27.125 10.875C27.7083 11.4583 28 12.1667 28 13C28 13.8333 27.7083 14.5417 27.125 15.125C26.5417 15.7083 25.8333 16 25 16C24.6 16 24.225 15.9292 23.875 15.7875C23.525 15.6458 23.2167 15.45 22.95 15.2L15.925 19.3C15.9583 19.4167 15.9792 19.5292 15.9875 19.6375C15.9958 19.7458 16 19.8667 16 20C16 20.1333 15.9958 20.2542 15.9875 20.3625C15.9792 20.4708 15.9583 20.5833 15.925 20.7L22.95 24.8C23.2167 24.55 23.525 24.3542 23.875 24.2125C24.225 24.0708 24.6 24 25 24C25.8333 24 26.5417 24.2917 27.125 24.875C27.7083 25.4583 28 26.1667 28 27C28 27.8333 27.7083 28.5417 27.125 29.125C26.5417 29.7083 25.8333 30 25 30ZM25 28C25.2833 28 25.5208 27.9042 25.7125 27.7125C25.9042 27.5208 26 27.2833 26 27C26 26.7167 25.9042 26.4792 25.7125 26.2875C25.5208 26.0958 25.2833 26 25 26C24.7167 26 24.4792 26.0958 24.2875 26.2875C24.0958 26.4792 24 26.7167 24 27C24 27.2833 24.0958 27.5208 24.2875 27.7125C24.4792 27.9042 24.7167 28 25 28ZM13 21C13.2833 21 13.5208 20.9042 13.7125 20.7125C13.9042 20.5208 14 20.2833 14 20C14 19.7167 13.9042 19.4792 13.7125 19.2875C13.5208 19.0958 13.2833 19 13 19C12.7167 19 12.4792 19.0958 12.2875 19.2875C12.0958 19.4792 12 19.7167 12 20C12 20.2833 12.0958 20.5208 12.2875 20.7125C12.4792 20.9042 12.7167 21 13 21ZM25 14C25.2833 14 25.5208 13.9042 25.7125 13.7125C25.9042 13.5208 26 13.2833 26 13C26 12.7167 25.9042 12.4792 25.7125 12.2875C25.5208 12.0958 25.2833 12 25 12C24.7167 12 24.4792 12.0958 24.2875 12.2875C24.0958 12.4792 24 12.7167 24 13C24 13.2833 24.0958 13.5208 24.2875 13.7125C24.4792 13.9042 24.7167 14 25 14Z" fill="currentColor" />
  </svg>
);

const PlayIconCustom = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="31" height="34" viewBox="0 0 31 34" fill="none" {...props}>
    <path d="M9.99997 24V10L21 17L9.99997 24ZM12 20.35L17.25 17L12 13.65V20.35Z" fill="currentColor" />
  </svg>
);

const EditCatalogIconList = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none" {...props}>
    <path d="M11.7167 7.5L12.5 8.28333L4.93333 15.8333H4.16667V15.0667L11.7167 7.5ZM14.7167 2.5C14.5083 2.5 14.2917 2.58333 14.1333 2.74167L12.6083 4.26667L15.7333 7.39167L17.2583 5.86667C17.5833 5.54167 17.5833 5 17.2583 4.69167L15.3083 2.74167C15.1417 2.575 14.9333 2.5 14.7167 2.5ZM11.7167 5.15833L2.5 14.375V17.5H5.625L14.8417 8.28333L11.7167 5.15833Z" fill="#7B7487" />
  </svg>
);

const DeleteCatalogIconList = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" {...props}>
    <g clipPath="url(#clip0_5076_2304)">
      <path d="M8.2161 0L9.9927 0.0045C10.6929 0.0891 11.3067 0.4185 11.8125 0.9747C12.2256 1.4283 12.4425 1.9557 12.4551 2.5299H17.3826C17.5471 2.53085 17.7046 2.59707 17.8203 2.71401C17.9361 2.83095 18.0007 2.98906 18 3.1536C18.0005 3.31799 17.9358 3.47586 17.82 3.59261C17.7043 3.70935 17.547 3.77545 17.3826 3.7764L15.7302 3.7755V14.2398C15.7302 16.5285 14.8761 18 12.9933 18H4.8771C2.9943 18 2.1537 16.5366 2.1537 14.2398V3.7755H0.6174C0.453325 3.77455 0.296295 3.7087 0.180612 3.59235C0.0649294 3.47599 -2.75696e-06 3.31858 0 3.1545C0 2.8098 0.2763 2.5317 0.6174 2.5317H5.5404C5.553 2.0691 5.7249 1.6002 6.039 1.1367C6.507 0.4455 7.2378 0.0666 8.2161 0ZM14.4954 3.7755H3.3876V14.2398C3.3876 15.939 3.8556 16.7544 4.8771 16.7544H12.9933C14.0184 16.7544 14.4963 15.9318 14.4963 14.2398L14.4954 3.7755ZM6.0444 5.706C6.3846 5.706 6.6609 5.985 6.6609 6.3288V13.5738C6.66138 13.7382 6.59666 13.8961 6.48092 14.0128C6.36519 14.1296 6.20789 14.1956 6.0435 14.1966C5.87927 14.1954 5.7222 14.1292 5.60666 14.0125C5.49112 13.8958 5.42652 13.738 5.427 13.5738V6.3288C5.427 5.985 5.7042 5.706 6.0444 5.706ZM8.4969 5.706C8.8389 5.706 9.1143 5.985 9.1143 6.3288V13.5738C9.11478 13.7382 9.05006 13.8961 8.93433 14.0128C8.81859 14.1296 8.66129 14.1956 8.4969 14.1966C8.33267 14.1954 8.1756 14.1292 8.06006 14.0125C7.94452 13.8958 7.87992 13.738 7.8804 13.5738V6.3288C7.8804 5.985 8.1567 5.706 8.4969 5.706ZM10.9521 5.706C11.2923 5.706 11.5686 5.985 11.5686 6.3288V13.5738C11.5691 13.738 11.5045 13.8958 11.3889 14.0125C11.2734 14.1292 11.1163 14.1954 10.9521 14.1966C10.7877 14.1956 10.6304 14.1296 10.5147 14.0128C10.3989 13.8961 10.3342 13.7382 10.3347 13.5738V6.3288C10.3342 6.16441 10.3989 6.00654 10.5147 5.88979C10.6304 5.77305 10.7877 5.70695 10.9521 5.706ZM8.2584 1.2438C7.6806 1.2843 7.2999 1.4814 7.0578 1.8396C6.8796 2.1015 6.7905 2.3283 6.7761 2.5308L11.2203 2.5299C11.2077 2.268 11.1042 2.0376 10.9035 1.8171C10.5921 1.4751 10.2465 1.2897 9.9198 1.2456L8.2584 1.2438Z" fill="#7B7487" />
    </g>
    <defs>
      <clipPath id="clip0_5076_2304">
        <rect width="18" height="18" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

// --- Sidebar (was CatalogList) ---

// Common placeholder box for missing/creation images
function NoImagePlaceholder({ width = 45, height = 45, borderRadius = '8px', iconSize = 18 }) {
  return (
    <Box
      sx={{
        width,
        height,
        borderRadius,
        bgcolor: '#E1E3E4',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}
    >
      <svg xmlns="http://www.w3.org/2000/svg" width={iconSize} height={iconSize} viewBox="0 0 18 18" fill="none">
        <path d="M2 18C1.45 18 0.979167 17.8042 0.5875 17.4125C0.195833 17.0208 0 16.55 0 16V2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0H16C16.55 0 17.0208 0.195833 17.4125 0.5875C17.8042 0.979167 18 1.45 18 2V16C18 16.55 17.8042 17.0208 17.4125 17.4125C17.0208 17.8042 16.55 18 16 18H2ZM2 16H16V2H2V16ZM3 14H15L11.25 9L8.25 13L6 10L3 14ZM2 16V2V16Z" fill="#7B7487" />
      </svg>
    </Box>
  );
}

// 2x2 thumbnail grid for sidebar catalog cards
function CatalogThumbGrid({ previewItems = [] }) {
  const slots = [0, 1, 2, 3];

  if (previewItems.length === 0) {
    return <NoImagePlaceholder width={45} height={45} borderRadius="8px" iconSize={18} />;
  }

  return (
    <Box
      sx={{
        width: 45,
        height: 45,
        flexShrink: 0,
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gridTemplateRows: '1fr 1fr',
        gap: '1px',
        borderRadius: '8px',
        overflow: 'hidden',
        bgcolor: '#E1E3E4',
      }}
    >
      {slots.map(i => (
        <Box
          key={i}
          sx={{
            bgcolor: '#E1E3E4',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {getCatalogThumbSrc(previewItems[i]) ? (
            <SecureImage
              src={getCatalogThumbSrc(previewItems[i])}
              alt=""
              useThumbnail={true}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <ImageOutlined sx={{ fontSize: 8, color: '#aaa', opacity: 0.6 }} />
          )}
        </Box>
      ))}
    </Box>
  );
}

// --- Added category section card ---
const CatalogCard = memo(function CatalogCard({ catalog, isActive, onSelect, onEdit, onDelete }) {
  const previewItems = catalog.preview_items?.length
    ? catalog.preview_items
    : [];

  return (
    <Box
      className="category-card added-category-card"
      onClick={() => onSelect(catalog.id)}
      sx={{
        height: '70px',
        boxSizing: 'border-box',
        px: 1.5,
        mb: 1,
        borderRadius: '12px',
        cursor: 'pointer',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        transition: 'all 0.15s ease',
        bgcolor: isActive ? 'rgba(124, 90, 237, 0.05)' : '#FFFFFF',
        border: isActive
          ? '1px solid rgba(124, 90, 237, 0.2)'
          : '1px solid rgba(204, 195, 216, 0.3)',
        '&:hover': {
          bgcolor: 'rgba(124, 90, 237, 0.05)',
          borderColor: 'rgba(124, 90, 237, 0.2)',
          '& .catalog-card-title': { color: '#7C3AED' },
          '& .catalog-card-actions': { display: 'flex' },
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
        <CatalogThumbGrid previewItems={previewItems} />
        <Box sx={{ flex: 1, minWidth: 0, pr: 7 }}>
          <Typography
            className="category-name catalog-card-title"
            noWrap
            sx={{
              fontFamily: 'var(--font-montserrat-semibold, Montserrat), sans-serif',
              fontWeight: 600,
              fontSize: '14px',
              color: isActive ? '#7C3AED' : '#191C1D',
              transition: 'color 0.15s ease',
            }}
          >
            {catalog.name}
          </Typography>
          <Typography
            className="category-count"
            sx={{
              fontFamily: 'var(--font-montserrat-medium, Montserrat), sans-serif',
              fontWeight: 500,
              fontSize: '12px',
              color: '#4A4455',
              mt: 0.25,
            }}
          >
            {catalog.item_count} Product{catalog.item_count !== 1 ? 's' : ''}
          </Typography>
        </Box>
      </Box>

      {/* Hover action buttons */}
      <Box
        className="catalog-card-actions"
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'absolute',
          right: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
          gap: '6px',
          alignItems: 'center',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >

        <IconButton
          className="category-delete-button"
          size="small"
          onClick={(e) => { e.stopPropagation(); onDelete && onDelete(catalog); }}
          sx={{
            p: '5px',
            bgcolor: '#FFFFFF',
            border: '1px solid #CCC3D8',
            borderRadius: '6px',
            width: { xs: "22px", md: "28px" },
            height: { xs: "22px", md: "28px" },
            '&:hover': { bgcolor: '#FFF0F0', borderColor: '#E53935', '& svg path': { fill: '#E53935' } },
          }}
        >
          <DeleteCatalogIconList />
        </IconButton>
      </Box>
    </Box>
  );
});

// Inline create row (dashed border + Save)
function InlineCreateCard({ onSaved, onCancel }) {
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Enter a catalog name');
      return;
    }
    if (trimmed.length > CATALOG_NAME_MAX_LENGTH) {
      setError(`Catalog name must be ${CATALOG_NAME_MAX_LENGTH} characters or less`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const res = await catalogAPI.createCatalog({ name: trimmed, description: '' });
      onSaved(res.data.catalog);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to create catalog');
    } finally {
      setSaving(false);
    }
  };

  return (
    // --- New category add section card ---
    <Box
      className="new-category-card"
      sx={{
        p: 1.5,
        pt: 3,
        mb: 1,
        borderRadius: '12px',
        border: `2px dashed ${catalogColors.purple}`,
        bgcolor: catalogColors.cardBg,
        position: 'relative',
      }}
    >
      <IconButton
        onClick={onCancel}
        size="small"
        className="card-close-btn"
        sx={{
          position: 'absolute',
          top: -2,
          padding: 0.5,
          border: '2px solid #E65B65',
          right: -2,
          width: 22,
          height: 22,
          zIndex: 2,
          borderRadius: '50%',
          bgcolor: '#fff',
          '&:hover': { bgcolor: '#fff' },
        }}
      >
        <svg className="new-category-card-close-btn" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
          <path d="M7.75702 16.243L16.243 7.75696M16.243 16.243L7.75702 7.75696" stroke="#E65B65" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </IconButton>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <NoImagePlaceholder width={45} height={45} borderRadius="8px" iconSize={18} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <TextField
            variant="standard"
            placeholder="Catalog name"
            value={name}
            onChange={e => setName(e.target.value)}
            fullWidth
            autoFocus
            disabled={saving}
            inputProps={{ maxLength: CATALOG_NAME_MAX_LENGTH }}
            helperText={`${name.length}/${CATALOG_NAME_MAX_LENGTH}`}
            FormHelperTextProps={{ sx: { textAlign: 'right', fontSize: 10 } }}
            onKeyDown={e => {
              if (e.key === 'Enter') handleSave();
              if (e.key === 'Escape') onCancel();
            }}
            sx={{
              '& .MuiInput-underline:before': { borderBottomColor: catalogColors.border },
              '& .MuiInput-underline:after': { borderBottomColor: catalogColors.purple },
              '& .MuiInputBase-input': { fontSize: 13, fontFamily: catalogFonts.semibold },
            }}
          />
          {error && (
            <Typography sx={{ fontSize: 11, color: '#E53935', mt: 0.5 }}>{error}</Typography>
          )}
        </Box>
        <Button
          className="save-button"
          onClick={handleSave}
          disabled={saving}
          size="small"
          sx={{
            bgcolor: catalogColors.purple,
            color: '#fff',
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            fontSize: 12,
            px: 2,
            minWidth: 64,
            '&:hover': { bgcolor: catalogColors.purpleDark },
          }}
        >
          {saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : 'Save'}
        </Button>
      </Box>
    </Box>
  );
}

function CatalogListPanel({
  catalogs,
  loading,
  hasMore,
  activeCatalogId,
  onSelect,
  onLoadMore,
  onCatalogCreated,
  onEditCatalog,
  onDeleteCatalog,
  snackbar,
  onSnackbarClose,
  canCreate = true,
}) {
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [showNextSkeleton, setShowNextSkeleton] = useState(false);
  const sentinelRef = useRef(null);

  const filtered = search.trim()
    ? catalogs.filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
    : catalogs;

  const handleObserver = useCallback(
    (entries) => {
      if (entries[0].isIntersecting && hasMore && !loading && !showNextSkeleton) {
        setShowNextSkeleton(true);
        setTimeout(() => {
          onLoadMore();
        }, 0);
      }
    },
    [hasMore, loading, onLoadMore, showNextSkeleton]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    const el = sentinelRef.current;
    if (el) observer.observe(el);
    return () => el && observer.unobserve(el);
  }, [handleObserver]);

  useEffect(() => {
    if (!loading) {
      setShowNextSkeleton(false);
    }
  }, [loading]);

  return (
    // --- Category list section ---
    <Box
      className="category-list-section"
      sx={{
        bgcolor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        p: 2,
        minHeight: { xs: "auto", sm: "auto", md: "480px" },
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography sx={{
          fontFamily: 'var(--font-montserrat-regular, Montserrat), sans-serif',
          fontWeight: 400,
          color: '#191C1D',
          fontSize: {
            xs: '15px',
            sm: '15px',
            md: '16px',
          },
        }}>
          {catalogs.length} Catalog{catalogs.length !== 1 ? 's' : ''}
        </Typography>
        {canCreate && <IconButton
          onClick={() => setCreating(true)}
          size="small"
          sx={{
            color: '#7C3AED',
            p: 0.5,
            '&:hover': { bgcolor: 'transparent', opacity: 0.8 },
          }}
        >
          <Add />
        </IconButton>}
      </Box>

      <TextField
        size="small"
        placeholder="Search catalogs"
        value={search}
        onChange={e => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIconCustom className='search-input-icon' sx={{ width: { xs: '30px', sm: '30px', md: '35px' }, height: { xs: '30px', sm: '30px', md: '35px' }, color: '#6B7280' }} />
            </InputAdornment>
          ),
          endAdornment: search ? (
            <InputAdornment position="end">
              <IconButton onClick={() => setSearch('')} edge="end" size="small">
                <Close fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null,
        }}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            bgcolor: '#F8F9FA',
            height: {
              xs: '42px',
              sm: '44px',
              md: '48px',
            },
            fontFamily: 'var(--font-montserrat-medium, Montserrat), sans-serif',
            fontSize: {
              xs: '14px',
              sm: '14px',
              md: '14px',
            },
            '& fieldset': {
              borderColor: '#CCC3D8',
              borderWidth: '1px',
            },
            '&:hover fieldset': {
              borderColor: '#CCC3D8',
              borderWidth: '1px',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#7C3AED',
              borderWidth: '1px',
            },
          },
          '& .MuiInputBase-input::placeholder': {
            color: '#6B7280',
            opacity: 1,
            fontFamily: 'var(--font-montserrat-medium, Montserrat), sans-serif',
            fontSize: '14px',
          },
        }}
        fullWidth
      />

      <Box sx={{ flex: 1, maxHeight: '500px', overflowY: 'auto' }}>
        {creating && (
          <InlineCreateCard
            onSaved={cat => {
              setCreating(false);
              onCatalogCreated(cat);
            }}
            onCancel={() => setCreating(false)}
          />
        )}

        {loading && catalogs.length === 0 ? (
          [1, 2, 3].map(i => (
            <Skeleton key={i} variant="rounded" height={72} sx={{ mb: 1, borderRadius: 2 }} />
          ))
        ) : filtered.length === 0 && !creating ? (
          <Box sx={{ textAlign: 'center', py: 5, color: catalogColors.textSecondary }}>
            <Typography sx={{ fontSize: 13 }}>
              {search ? 'No catalogs match your search' : 'No catalogs yet'}
            </Typography>
            {!search && canCreate && (
              <Button
                onClick={() => setCreating(true)}
                sx={{ mt: 1, textTransform: 'none', color: catalogColors.purple, fontSize: 13 }}
              >
                Create your first catalog
              </Button>
            )}
          </Box>
        ) : (
          filtered.map(catalog => (
            <CatalogCard
              key={catalog.id}
              catalog={catalog}
              isActive={catalog.id === activeCatalogId}
              onSelect={onSelect}
              onEdit={onEditCatalog}
              onDelete={onDeleteCatalog}
            />
          ))
        )}

        <Box ref={sentinelRef} sx={{ height: 8 }} />
        {showNextSkeleton && (
          <Box sx={{ mt: 1 }}>
            {[1, 2, 3].map(i => (
              <Skeleton key={`next-cat-${i}`} variant="rounded" height={72} sx={{ mb: 1, borderRadius: 2 }} />
            ))}
          </Box>
        )}
      </Box>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={3000}
        onClose={onSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={onSnackbarClose} severity={snackbar?.severity || 'info'} sx={{ width: '100%' }}>
          {snackbar?.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// --- Product cards ---

// --- Product row (was CatalogItemCard) ---

// Lazy-load catalog media (presigned S3 or thumbnail API — SecureImage handles loading/errors)
function CatalogMediaThumb({ item, alt }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const thumbSrc = getCatalogThumbSrc(item);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.1, rootMargin: '120px' }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <Box
      className="product-image-section"
      ref={ref}
      sx={{
        width: { xs: 70, md: 80 },
        height: { xs: 110, md: 110 },
        flexShrink: 0,
        borderRadius: '8px',
        overflow: 'hidden',
        bgcolor: 'rgba(204, 195, 216, 0.2)',
        border: '1px solid rgba(204, 195, 216, 0.2)',
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {visible && thumbSrc ? (
        <>
          <SecureImage
            src={thumbSrc}
            alt={alt}
            useThumbnail={true}
            style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'top', display: 'block' }}
          />
          {item?.media_type === 'video' && (
            <PlayCircleOutline
              sx={{
                position: 'absolute',
                bottom: 4,
                right: 4,
                color: '#fff',
                fontSize: 18,
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.5))',
                pointerEvents: 'none',
              }}
            />
          )}
        </>
      ) : (
        <NoImagePlaceholder width="100%" height="100%" borderRadius="8px" iconSize={24} />
      )}
    </Box>
  );
}

const CatalogItemRow = memo(function CatalogItemRow({ item, index, isActive, onSelect, onEdit, onDelete }) {
  const title = formatProductLabel(item);
  const { caption1, caption2 } = getCatalogItemListLines(item);
  const lineSx = {
    fontFamily: 'var(--font-montserrat-regular, Montserrat), sans-serif',
    fontSize: { xs: '13px', md: '14px' },
    color: '#7B7487',
    mt: 0.25,
  };

  return (
    <Box
      className="product-card"
      onClick={() => onSelect && onSelect(item)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1.5, md: 2 },
        p: 1.5,
        mb: 1.5,
        bgcolor: isActive ? 'rgba(124, 90, 237, 0.05)' : '#FFFFFF',
        border: isActive ? '1px solid rgba(124, 90, 237, 0.2)' : '1px solid rgba(204, 195, 216, 0.3)',
        borderRadius: '12px',
        cursor: 'pointer',
        position: 'relative',
        transition: 'all 0.15s ease',
        '&:hover': {
          bgcolor: 'rgba(124, 90, 237, 0.05)',
          borderColor: 'rgba(124, 90, 237, 0.2)',
          '& .action-buttons': { display: 'flex' },
        },
      }}
    >
      <CatalogMediaThumb item={item} alt={title} />

      <Box sx={{ flex: 1, minWidth: 0, pr: { xs: 5, md: 10 } }}>
        {/* <Typography
          sx={{
            fontFamily: 'var(--font-montserrat-semibold, Montserrat), sans-serif',
            fontWeight: 600,
            fontSize: { xs: '13px', md: '16px' },
            color: '#191C1D',
            letterSpacing: 0.3,
          }}
        >
          {title}
        </Typography> */}
        <Typography sx={{ ...lineSx, fontFamily: 'var(--font-freeserif)' }} noWrap>
          {caption1}
        </Typography>
        <Typography sx={{ ...lineSx, fontFamily: 'var(--font-freesans)' }} noWrap>
          {caption2}
        </Typography>
      </Box>

      <Box
        className="action-buttons"
        sx={{
          display: { xs: 'flex', md: 'none' },
          position: 'absolute',
          right: '10px',
          top: '50%',
          transform: 'translateY(-50%)',
          gap: '6px',
          alignItems: 'center',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >

        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); onDelete && onDelete(item); }}
          sx={{
            p: '5px',
            bgcolor: '#FFFFFF',
            border: '1px solid #CCC3D8',
            borderRadius: '6px',
            width: { xs: "22px", md: "28px" },
            height: { xs: "22px", md: "28px" },
            '&:hover': { bgcolor: '#FFF0F0', borderColor: '#E53935', '& svg path': { fill: '#E53935' } },
          }}
        >
          <DeleteCatalogIconList />
        </IconButton>
      </Box>
    </Box>
  );
});

// --- Detail panel (was CatalogDetail) ---

const itemsInit = { items: [], nextCursor: null, hasMore: false, loading: false };

function itemsReducer(state, action) {
  switch (action.type) {
    case 'LOADING':
      return { ...state, loading: true };
    case 'SET':
      return {
        items: action.payload.items,
        nextCursor: action.payload.next_cursor,
        hasMore: action.payload.has_more,
        loading: false,
      };
    case 'APPEND':
      return {
        items: [...state.items, ...action.payload.items],
        nextCursor: action.payload.next_cursor,
        hasMore: action.payload.has_more,
        loading: false,
      };
    case 'UPDATE_ITEM':
      return {
        ...state,
        items: state.items.map(i =>
          i.id === action.payload.id ? { ...i, ...action.payload } : i
        ),
      };
    case 'REMOVE_ITEM':
      return { ...state, items: state.items.filter(i => i.id !== action.payload) };
    case 'RESET':
      return itemsInit;
    default:
      return state;
  }
}

// --- Product details section ---
function ProductDetailsPanel({ product, catalogId, onClose, onUpdated, onSnackbar }) {
  const [productName, setProductName] = useState('');
  const [caption1, setCaption1] = useState('');
  const [caption2, setCaption2] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setProductName(product.product_name && product.product_name !== 'NA' ? product.product_name : '');
    setCaption1(product.caption_1 && product.caption_1 !== 'NA' ? product.caption_1 : '');
    setCaption2(product.caption_2 && product.caption_2 !== 'NA' ? product.caption_2 : '');
  }, [product.id, product.product_name, product.caption_1, product.caption_2]);

  const fieldValue = (field) => {
    if (field === 'product_name') {
      return product.product_name && product.product_name !== 'NA' ? product.product_name : '';
    }
    if (field === 'caption_1') {
      return product.caption_1 && product.caption_1 !== 'NA' ? product.caption_1 : '';
    }
    return product.caption_2 && product.caption_2 !== 'NA' ? product.caption_2 : '';
  };

  const handleBlur = async (field, value) => {
    const trimmed = value.trim();
    if (trimmed === (fieldValue(field) || '')) {
      return;
    }

    const storedOrNull = (val) => (val && val !== 'NA' ? val : null);

    try {
      setSaving(true);
      const payload = {
        product_name: field === 'product_name'
          ? (trimmed || null)
          : storedOrNull(product.product_name),
        caption_1: field === 'caption_1'
          ? (trimmed || null)
          : storedOrNull(product.caption_1),
        caption_2: field === 'caption_2'
          ? (trimmed || null)
          : storedOrNull(product.caption_2),
      };
      const res = await catalogAPI.updateItem(catalogId, product.id, payload);
      onUpdated({ ...product, ...res.data.item });
    } catch (err) {
      onSnackbar({ message: 'Failed to update product details', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const previewSrc = getCatalogPreviewSrc(product);

  return (
    <Box
      className="product-details-section"
      sx={{
        bgcolor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        minHeight: 480,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Inner bordered container — matches CreateEditCatalogDialog */}
      <Box
        sx={{
          m: 0,
          pt: 2.5,
          px: 2.8,
          pb: 2.5,
          border: 'none',
          borderRadius: '12px',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          flex: 1,
          overflowY: 'auto',
        }}
      >
        {/* Close button — top-right of inner box */}
        <IconButton
          onClick={onClose}
          sx={{
            position: 'absolute',
            top: -2,
            right: 0,
            width: 36,
            height: 36,
            borderRadius: '50%',
            bgcolor: '#F4F2F7',
            '&:hover': { bgcolor: '#EBE9ED' },
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M7.75702 16.243L16.243 7.75696M16.243 16.243L7.75702 7.75696" stroke="#111827" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </IconButton>

        {/* Image */}
        <Box
          className="product-details-image-section"
          sx={{
            width: '100%',
            height: 'auto',
            borderRadius: '8px',
            overflow: 'hidden',
            bgcolor: 'rgba(204, 195, 216, 0.2)',
            border: '1px solid rgba(204, 195, 216, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {previewSrc ? (
            <SecureImage
              key={`catalog-item-img-${product.ai_generation_id}`}
              src={previewSrc}
              alt={product.product_name !== 'NA' ? product.product_name : 'Product image'}
              useThumbnail={product.media_type === 'video'}
              thumbnailSize={product.media_type === 'video' ? 'thumbnail' : '1200x1200'}
              style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
            />
          ) : (
            <NoImagePlaceholder width="100%" height="100%" borderRadius="8px" iconSize={36} />
          )}
        </Box>

        {/* Product Name card */}
        {/* <Box
          sx={{
            border: '1px solid rgba(204, 195, 216, 0.3)',
            borderRadius: '12px',
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'var(--font-montserrat-regular, Montserrat), sans-serif',
              fontWeight: 400,
              fontSize: '13px',
              color: '#7B7487',
            }}
          >
            Product Name
          </Typography>
          <TextField
            variant="standard"
            value={productName}
            onChange={(e) => setProductName(e.target.value)}
            onBlur={() => handleBlur('product_name', productName)}
            fullWidth
            disabled={saving}
            placeholder="Enter product name"
            InputProps={{ disableUnderline: true }}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'var(--font-montserrat-semibold, Montserrat), sans-serif',
                fontWeight: 600,
                fontSize: {
                  xs: '14px',
                  md: '16px',
                },
                color: '#191C1D',
                borderBottom: '2px solid #191C1D',
                pb: 0.5,
              },
            }}
          />
        </Box> */}

        {/* Product Caption card */}
        <Box
          sx={{
            border: '1px solid rgba(204, 195, 216, 0.3)',
            borderRadius: '12px',
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'var(--font-montserrat-regular, Montserrat), sans-serif',
              fontWeight: 400,
              fontSize: '13px',
              color: '#7B7487',
            }}
          >
            Product Caption 1
          </Typography>
          <TextField
            variant="standard"
            value={caption1}
            fullWidth
            disabled={true}
            placeholder="No Caption 1 from gallery"
            InputProps={{ disableUnderline: true }}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'var(--font-montserrat-semibold, Montserrat), sans-serif',
                fontWeight: 600,
                fontSize: {
                  xs: '14px',
                  md: '16px',
                },
                color: '#7B7487',
                borderBottom: '2px solid #CCC3D8',
                pb: 0.5,
              },
            }}
          />
        </Box>

        <Box
          sx={{
            border: '1px solid rgba(204, 195, 216, 0.3)',
            borderRadius: '12px',
            p: 1.5,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          <Typography
            sx={{
              fontFamily: 'var(--font-montserrat-regular, Montserrat), sans-serif',
              fontWeight: 400,
              fontSize: '13px',
              color: '#7B7487',
            }}
          >
            Product Caption 2
          </Typography>
          <TextField
            variant="standard"
            value={caption2}
            fullWidth
            disabled={true}
            placeholder="No Caption 2 from gallery"
            InputProps={{ disableUnderline: true }}
            sx={{
              '& .MuiInputBase-input': {
                fontFamily: 'var(--font-montserrat-semibold, Montserrat), sans-serif',
                fontWeight: 600,
                fontSize: {
                  xs: '14px',
                  md: '16px',
                },
                color: '#7B7487',
                borderBottom: '2px solid #CCC3D8',
                pb: 0.5,
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}

// --- Product list section ---
function CatalogDetailPanel({
  catalog,
  onBack,
  onEditCatalog,
  onAddFromGallery,
  onItemCountChanged,
  onSnackbar,
  onCatalogPreviewsUpdated,
  selectedProduct,
  onProductSelect,
  onItemListPatchReady,
  canShare = true,
  canDelete = true,
}) {
  const [itemsState, dispatchItems] = useReducer(itemsReducer, itemsInit);
  const [editItem, setEditItem] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [itemSearch, setItemSearch] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [showNextSkeleton, setShowNextSkeleton] = useState(false);

  const sentinelRef = useRef(null);
  const abortRef = useRef(null);

  const patchItemInList = useCallback((patch) => {
    if (patch?.id) {
      dispatchItems({ type: 'UPDATE_ITEM', payload: patch });
    }
  }, []);

  useEffect(() => {
    onItemListPatchReady?.(patchItemInList);
  }, [onItemListPatchReady, patchItemInList]);

  const loadItems = useCallback(async (cursor = null, replace = true) => {
    if (!catalog) return;
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    dispatchItems({ type: 'LOADING' });
    try {
      const params = { limit: 6 };
      if (cursor) params.cursor = cursor;
      const res = await catalogAPI.getCatalogItems(
        catalog.id,
        params,
        abortRef.current.signal
      );
      if (replace) {
        dispatchItems({ type: 'SET', payload: res.data });
        // Refresh sidebar preview thumbnails after items load
        if (onCatalogPreviewsUpdated && res.data.items?.length) {
          const previewItems = res.data.items.slice(0, 4).map(i => ({
            ai_generation_id: i.ai_generation_id,
            media_type: i.media_type,
            has_enhanced_poster: i.has_enhanced_poster,
          }));
          onCatalogPreviewsUpdated(catalog.id, previewItems);
        }
      } else {
        dispatchItems({ type: 'APPEND', payload: res.data });
      }
    } catch (err) {
      if (err.name !== 'CanceledError' && err.code !== 'ERR_CANCELED') {
        dispatchItems({ type: 'RESET' });
        onSnackbar({ message: 'Failed to load items', severity: 'error' });
      }
    } finally {
      setShowNextSkeleton(false);
    }
  }, [catalog, onSnackbar, onCatalogPreviewsUpdated]);

  useEffect(() => {
    onProductSelect?.(null);
    if (catalog) {
      setItemSearch('');
      setSearchOpen(false);
      loadItems();
    } else {
      dispatchItems({ type: 'RESET' });
    }
    return () => { if (abortRef.current) abortRef.current.abort(); };
  }, [catalog?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleObserver = useCallback(
    (entries) => {
      if (entries[0].isIntersecting && itemsState.hasMore && !itemsState.loading && !showNextSkeleton) {
        setShowNextSkeleton(true);
        setTimeout(() => {
          loadItems(itemsState.nextCursor, false);
        }, 0);
      }
    },
    [itemsState.hasMore, itemsState.loading, itemsState.nextCursor, loadItems, showNextSkeleton]
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    const el = sentinelRef.current;
    if (el) observer.observe(el);
    return () => el && observer.unobserve(el);
  }, [handleObserver]);

  const handleConfirmDeleteItem = async () => {
    if (!confirmDel) return;
    try {
      const res = await catalogAPI.removeItem(catalog.id, confirmDel.id);
      dispatchItems({ type: 'REMOVE_ITEM', payload: confirmDel.id });
      if (selectedProduct && selectedProduct.id === confirmDel.id) {
        onProductSelect?.(null);
      }
      onItemCountChanged(catalog.id, res.data.item_count);
      onSnackbar({ message: 'Item removed', severity: 'info' });
      loadItems();
    } catch {
      onSnackbar({ message: 'Failed to remove item', severity: 'error' });
    } finally {
      setConfirmDel(null);
    }
  };

  const filteredItems = itemSearch.trim()
    ? itemsState.items.filter(item => {
      const q = itemSearch.toLowerCase();
      const label = `product${String(item.sort_order + 1).padStart(4, '0')}`;
      return (
        label.includes(q)
        || (item.product_name && item.product_name !== 'NA' && item.product_name.toLowerCase().includes(q))
        || (item.caption_1 && item.caption_1.toLowerCase().includes(q))
        || (item.caption_2 && item.caption_2.toLowerCase().includes(q))
      );
    })
    : itemsState.items;

  const handlePlayClick = async () => {
    try {
      const res = await catalogAPI.getShareLinks(catalog.id);
      const links = res.data.share_links || [];
      const activeLink = links.find(l => !l.is_expired && l.is_active);
      const token = activeLink
        ? activeLink.token
        : (await catalogAPI.createShareLink(catalog.id, { label: 'Quick View' })).data.share_link.token;
      const previewUrl = `${getBaseUrl()}/catalog/${token}`;
      await FlutterServicesAPI.openExternalBrowser(previewUrl);
    } catch (err) {
      onSnackbar({ message: 'Failed to open preview link', severity: 'error' });
    }
  };

  if (!catalog) {
    return (
      <Box
        sx={{
          bgcolor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '12px',
          p: 6,
          textAlign: 'center',
          color: catalogColors.textSecondary,
          minHeight: { xs: "auto", sm: "auto", md: "480px" },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CollectionsBookmark sx={{ fontSize: 56, color: '#D1C4E9', mb: 2 }} />
        <Typography sx={{ fontWeight: 600, fontSize: 16, mb: 1, color: catalogColors.textPrimary }}>
          Select a Catalog
        </Typography>
        <Typography sx={{ fontSize: 13 }}>
          Choose a catalog from the list to view and manage its products.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      className="product-list-section"
      sx={{
        bgcolor: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '12px',
        overflow: 'hidden',
        minHeight: 480,
      }}
    >
      {/* White header — Figma */}
      <Box
        sx={{
          px: { xs: 1.5, md: 4.5 },
          py: { xs: 2.5, md: 3.5 },
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'flex-start' }, width: '100%', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'top', width: '100%', gap: 1 }}>
            {onBack && (
              <IconButton onClick={onBack} size="small" p={0} sx={{ mt: 0.5, mr: { xs: 0, md: 0.5 }, display: { md: 'none' } }}>
                <ArrowBack sx={{ width: { xs: "22px", md: "1.2rem" }, height: { xs: "22px", md: "1.2rem" } }} />
              </IconButton>
            )}

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontFamily: 'var(--font-montserrat-semibold, Montserrat), sans-serif',
                  fontWeight: 600,
                  fontSize: { xs: "14px", md: "16px" },
                  color: '#111827',
                  lineHeight: 1.2,
                }}
              >
                {catalog.name}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'var(--font-montserrat)',
                  fontWeight: 400,
                  fontSize: { xs: "13px", md: "15px" },
                  color: '#4A4455',
                  mt: 1,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {catalog.description || 'No description added'}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0, ml: { xs: onBack ? 5 : 0, md: 0 }, mt: { xs: 1, md: 0 } }}>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2 }}>
              <Tooltip title="Edit catalog">
                <IconButton
                  sx={{ width: { xs: 30, md: 45 }, height: { xs: 30, md: 45 }, p: { xs: '0 !important', md: '5px' }, color: '#4A4455' }}
                  onClick={() => onEditCatalog(catalog)}>
                  <EditIconCustom />
                </IconButton>
              </Tooltip>
              <Tooltip title="Search products">
                <IconButton
                  sx={{ width: { xs: 30, md: 45 }, height: { xs: 30, md: 45 }, p: { xs: '0 !important', md: '5px' }, color: searchOpen ? catalogColors.purple : '#4A4455' }}
                  onClick={() => setSearchOpen(v => !v)}
                >
                  <SearchIconCustom />
                </IconButton>
              </Tooltip>
              {canShare && (
                <Tooltip title="Share catalog">
                  <IconButton sx={{ width: { xs: 30, md: 45 }, height: { xs: 30, md: 45 }, p: { xs: '0 !important', md: '5px' }, color: '#4A4455' }} onClick={() => setShareOpen(true)}>
                    <ShareIconCustom />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="View public catalog">
                <IconButton sx={{ width: { xs: 30, md: 45 }, height: { xs: 30, md: 45 }, p: { xs: '0 !important', md: '5px' }, color: '#4A4455' }} onClick={handlePlayClick}>
                  <PlayIconCustom />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

      </Box>

      {/* Product List Body */}
      <Box sx={{ borderTop: `1px solid ${catalogColors.border}` }}>
        {/* In-catalog search */}
        {searchOpen && (
          <Box sx={{ px: 4, py: 2, borderBottom: `1px solid ${catalogColors.border}` }}>
            <TextField
              className='search-input'
              size="small"
              placeholder="Search products in this catalog"
              value={itemSearch}
              onChange={e => setItemSearch(e.target.value)}
              fullWidth
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIconCustom className='search-input-icon' sx={{ width: { xs: '28px', md: '35px' }, height: { xs: '28px', md: '35px' }, color: '#4A4455' }} />
                  </InputAdornment>
                ),
                endAdornment: itemSearch ? (
                  <InputAdornment position="end">
                    <IconButton onClick={() => { setItemSearch(''); setSearchOpen(false); }} edge="end" size="small">
                      <Close fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  bgcolor: '#F8F9FA',
                  height: { xs: '42px', md: '48px' },
                  fontFamily: 'var(--font-montserrat-medium, Montserrat), sans-serif',
                  fontSize: { xs: '12px', md: '14px' },
                  '& fieldset': {
                    borderColor: '#CCC3D8',
                    borderWidth: '1px',
                  },
                  '&:hover fieldset': {
                    borderColor: '#CCC3D8',
                    borderWidth: '1px',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#7C3AED',
                    borderWidth: '1px',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: '#6B7280',
                  opacity: 1,
                  fontFamily: 'var(--font-montserrat-medium, Montserrat), sans-serif',
                  fontSize: { xs: '12px', md: '14px' },
                },
              }}
            />
          </Box>
        )}

        {/* Product rows */}
        <Box sx={{ p: { xs: 1.2, md: 3 }, maxHeight: '556px', overflowY: 'auto' }}>
          {itemsState.loading && itemsState.items.length === 0 ? (
            [1, 2, 3, 4, 5].map(i => (
              <Skeleton key={i} variant="rounded" height={100} sx={{ mb: 1.5, borderRadius: 2.5 }} />
            ))
          ) : filteredItems.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <AddPhotoAlternate sx={{ fontSize: 48, color: '#D1C4E9', mb: 2 }} />
              <Typography sx={{ fontWeight: 600, fontSize: { xs: '12px', md: '15px' }, color: catalogColors.textPrimary, mb: 1 }}>
                {itemSearch ? 'No matching products' : 'No products yet'}
              </Typography>
              {!itemSearch && (
                <Button
                  onClick={() => onAddFromGallery?.(catalog)}
                  sx={{
                    bgcolor: catalogColors.purple,
                    color: '#fff',
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    fontSize: { xs: '12px', md: '15px' },
                    '&:hover': { bgcolor: catalogColors.purpleDark },
                  }}
                >
                  Add from Gallery
                </Button>
              )}
            </Box>
          ) : (
            filteredItems.map((item) => (
              <CatalogItemRow
                key={item.id}
                item={item}
                index={itemsState.items.indexOf(item)}
                isActive={selectedProduct && selectedProduct.id === item.id}
                onSelect={(selected) => onProductSelect?.(selected)}
                onEdit={(item) => onProductSelect?.(item)}
                onDelete={setConfirmDel}
              />
            ))
          )}

          <Box ref={sentinelRef} sx={{ height: 8 }} />
          {showNextSkeleton && (
            <Box sx={{ mt: 1 }}>
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={`next-${i}`} variant="rounded" height={100} sx={{ mb: 1.5, borderRadius: 2.5 }} />
              ))}
            </Box>
          )}
        </Box>
      </Box>



      <ShareLinkDialog open={shareOpen} catalog={catalog} onClose={() => setShareOpen(false)} />

      <Dialog open={Boolean(confirmDel)} onClose={() => setConfirmDel(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, fontFamily: catalogFonts.semibold }}>Remove Product?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: catalogColors.textSecondary }}>
            This product will be removed from the catalog. The original generation will not be deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDel(null)} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleConfirmDeleteItem} color="error" sx={{ textTransform: 'none', fontWeight: 600 }}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// --- Page state ---

const initialState = {
  catalogs: [],
  nextCursor: null,
  hasMore: false,
  loadingCatalogs: false,
  activeCatalogId: null,
  createDialogOpen: false,
  editCatalog: null,
  deleteConfirmOpen: false,
  catalogToDelete: null,
  snackbar: null,
};

function reducer(state, action) {
  switch (action.type) {
    case 'SET_LOADING_CATALOGS':
      return { ...state, loadingCatalogs: action.payload };
    case 'SET_CATALOGS':
      return {
        ...state,
        catalogs: action.payload.catalogs,
        nextCursor: action.payload.next_cursor,
        hasMore: action.payload.has_more,
        loadingCatalogs: false,
      };
    case 'APPEND_CATALOGS':
      return {
        ...state,
        catalogs: [...state.catalogs, ...action.payload.catalogs],
        nextCursor: action.payload.next_cursor,
        hasMore: action.payload.has_more,
        loadingCatalogs: false,
      };
    case 'ADD_CATALOG':
      return {
        ...state,
        catalogs: [action.payload, ...state.catalogs],
        activeCatalogId: action.payload.id,
      };
    case 'UPDATE_CATALOG':
      return {
        ...state,
        catalogs: state.catalogs.map(c =>
          c.id === action.payload.id ? { ...c, ...action.payload } : c
        ),
        editCatalog: null,
        createDialogOpen: false,
      };
    case 'UPDATE_CATALOG_PREVIEWS':
      return {
        ...state,
        catalogs: state.catalogs.map(c =>
          c.id === action.payload.id
            ? { ...c, preview_items: action.payload.preview_items }
            : c
        ),
      };
    case 'REMOVE_CATALOG':
      return {
        ...state,
        catalogs: state.catalogs.filter(c => c.id !== action.payload),
        activeCatalogId: state.activeCatalogId === action.payload ? null : state.activeCatalogId,
      };
    case 'SET_ACTIVE_CATALOG':
      return { ...state, activeCatalogId: action.payload };
    case 'OPEN_EDIT_DIALOG':
      return { ...state, createDialogOpen: true, editCatalog: action.payload };
    case 'CLOSE_DIALOG':
      return { ...state, createDialogOpen: false, editCatalog: null };
    case 'UPDATE_ITEM_COUNT':
      return {
        ...state,
        catalogs: state.catalogs.map(c =>
          c.id === action.payload.id ? { ...c, item_count: action.payload.item_count } : c
        ),
      };
    case 'OPEN_DELETE_CONFIRM':
      return { ...state, deleteConfirmOpen: true, catalogToDelete: action.payload };
    case 'CLOSE_DELETE_CONFIRM':
      return { ...state, deleteConfirmOpen: false, catalogToDelete: null };
    case 'SET_SNACKBAR':
      return { ...state, snackbar: action.payload };
    default:
      return state;
  }
}

export default function CatalogPage() {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const patchItemInListRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const { hasPermission } = useMerchantPermissions();
  const canCreate = hasPermission('Catalog Management', 'create');
  const canEdit   = hasPermission('Catalog Management', 'edit');
  const canDelete = hasPermission('Catalog Management', 'delete');
  const canShare  = hasPermission('Catalog Management', 'share');

  const loadCatalogs = useCallback(async (cursor = null, replace = true) => {
    dispatch({ type: 'SET_LOADING_CATALOGS', payload: true });
    try {
      const params = { limit: 6 };
      if (cursor) params.cursor = cursor;
      const res = await catalogAPI.getCatalogs(params);
      if (replace) {
        dispatch({ type: 'SET_CATALOGS', payload: res.data });
      } else {
        dispatch({ type: 'APPEND_CATALOGS', payload: res.data });
      }
    } catch {
      dispatch({ type: 'SET_LOADING_CATALOGS', payload: false });
      dispatch({ type: 'SET_SNACKBAR', payload: { message: 'Failed to load catalogs', severity: 'error' } });
    }
  }, []);

  useEffect(() => {
    loadCatalogs();
  }, [loadCatalogs]);

  // Return from Gallery after adding items (run once per navigation payload)
  const handledGalleryReturnRef = useRef(null);
  useEffect(() => {
    const s = location.state;
    if (!s?.activeCatalogId && !s?.snackbar) return;

    const key = `${s.activeCatalogId}-${s.itemCount}-${s.snackbar?.message || ''}`;
    if (handledGalleryReturnRef.current === key) return;
    handledGalleryReturnRef.current = key;

    if (s.activeCatalogId) {
      dispatch({ type: 'SET_ACTIVE_CATALOG', payload: s.activeCatalogId });
    }
    if (s.itemCount != null && s.activeCatalogId) {
      dispatch({ type: 'UPDATE_ITEM_COUNT', payload: { id: s.activeCatalogId, item_count: s.itemCount } });
      loadCatalogs();
    }
    if (s.snackbar) {
      dispatch({ type: 'SET_SNACKBAR', payload: s.snackbar });
    }

    navigate('/catalogs', { replace: true, state: {} });
  }, [location.state, navigate, loadCatalogs]);

  const activeCatalog = state.catalogs.find(c => c.id === state.activeCatalogId) || null;

  const goToGalleryForCatalog = async (catalog) => {
    if (!catalog) return;
    let existingGenerationIds = [];
    try {
      const res = await catalogAPI.getCatalogItems(catalog.id, { limit: 200 });
      existingGenerationIds = (res.data.items || []).map(i => i.ai_generation_id);
    } catch {
      existingGenerationIds = [];
    }
    navigate('/gallery', {
      state: buildAddToCatalogState({
        catalogId: catalog.id,
        catalogName: catalog.name,
        existingGenerationIds,
      }),
    });
  };

  const handleItemCountChanged = (catalogId, itemCount) => {
    dispatch({ type: 'UPDATE_ITEM_COUNT', payload: { id: catalogId, item_count: itemCount } });
  };

  const handleDeleteCatalog = async () => {
    const cat = state.catalogToDelete;
    if (!cat) return;
    try {
      await catalogAPI.deleteCatalog(cat.id);
      dispatch({ type: 'REMOVE_CATALOG', payload: cat.id });
      dispatch({ type: 'CLOSE_DELETE_CONFIRM' });
      dispatch({ type: 'SET_SNACKBAR', payload: { message: `"${cat.name}" deleted`, severity: 'success' } });
    } catch {
      dispatch({ type: 'SET_SNACKBAR', payload: { message: 'Failed to delete catalog', severity: 'error' } });
      dispatch({ type: 'CLOSE_DELETE_CONFIRM' });
    }
  };

  return (
    <>
      <DynamicMerchantTabs />

      <Box sx={{ width: '100%', px: { xs: 0, sm: 0, md: 0 }, mx: 0 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: '18px 21px', sm: '32px', md: '32px' },
            border: '1px solid #e5e7eb',
            boxShadow: 'none',
            backgroundColor: 'white',
            borderRadius: '12px',
          }}
        >
          {/* Title + action — inside card (same as Gallery / History) */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            flexWrap={'wrap'}
            gap={2}
            mb={{ xs: 2, sm: 2, md: 4 }}
          >
            <Box>
              <Typography
                variant="h6"
                fontWeight={700}
                fontSize={{ xs: '18px', sm: '24px', md: '28px' }}
                color="#111827"
                fontFamily="var(--font-montserrat-semibold)"
                sx={{ mb: 0.5 }}
              >
                Catalogue
              </Typography>
              <Typography
                fontSize={{ xs: '14px', sm: '15px', md: '17px' }}
                mb={{ xs: 0, sm: 0, md: 1 }}
                sx={{
                  color: '#4A4455',
                  fontFamily: 'var(--font-montserrat)',
                  fontWeight: 400,
                }}
              >
                Curate your AI-generated jewellery into shareable catalog links.
              </Typography>
            </Box>

            <Button
              disabled={!activeCatalog}
              onClick={() => goToGalleryForCatalog(activeCatalog)}
              startIcon={
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
                  <path d="M1.66667 15C1.20833 15 0.815972 14.8368 0.489583 14.5104C0.163194 14.184 0 13.7917 0 13.3333V1.66667C0 1.20833 0.163194 0.815972 0.489583 0.489583C0.815972 0.163194 1.20833 0 1.66667 0H8.33333C8.33333 0.236111 8.33333 0.493056 8.33333 0.770833C8.33333 1.04861 8.33333 1.34722 8.33333 1.66667H1.66667V13.3333H13.3333V6.66667C13.6528 6.66667 13.9514 6.66667 14.2292 6.66667C14.5069 6.66667 14.7639 6.66667 15 6.66667V13.3333C15 13.7917 14.8368 14.184 14.5104 14.5104C14.184 14.8368 13.7917 15 13.3333 15H1.66667ZM2.5 11.6667H12.5L9.375 7.5L6.875 10.8333L5 8.33333L2.5 11.6667ZM11.6667 5V3.33333H10V1.66667H11.6667V0H13.3333V1.66667H15V3.33333H13.3333V5H11.6667Z" fill="#191C1D" />
                </svg>
              }
              sx={{
                fontSize: {
                  xs: '14px',
                  sm: '14px',
                  md: '16px',
                },

                px: {
                  xs: 2,
                  sm: 3,
                  md: 4,
                },

                height: {
                  xs: '40px',
                  sm: '44px',
                  md: '48px',
                },

                bgcolor: 'transparent',
                color: '#191C1D',
                borderRadius: '12px',
                border: '1px solid #CCC3D8',
                fontFamily: 'var(--font-inter-regular)',
                textTransform: 'none',
                fontWeight: 400,

                '&:hover': {
                  bgcolor: 'rgba(229, 231, 235, 1)',
                },

                '&.Mui-disabled': {
                  opacity: 0.5,
                },
              }}
            >
              Add from Gallery
            </Button>
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              gap: 3,
              alignItems: 'flex-start',
            }}
          >
            {/* Column 1: Catalog List */}
            <Box
              sx={{
                width: isMobile ? '100%' : 330,
                flexShrink: 0,
                display: isMobile && state.activeCatalogId ? 'none' : 'block',
              }}
            >
              <CatalogListPanel
                catalogs={state.catalogs}
                loading={state.loadingCatalogs}
                hasMore={state.hasMore}
                activeCatalogId={state.activeCatalogId}
                onSelect={id => {
                  dispatch({ type: 'SET_ACTIVE_CATALOG', payload: id });
                  setSelectedProduct(null);
                }}
                onLoadMore={() => loadCatalogs(state.nextCursor, false)}
                onCatalogCreated={cat => {
                  dispatch({ type: 'ADD_CATALOG', payload: cat });
                  dispatch({ type: 'SET_SNACKBAR', payload: { message: 'Catalog created!', severity: 'success' } });
                }}
                onEditCatalog={canEdit ? c => dispatch({ type: 'OPEN_EDIT_DIALOG', payload: c }) : null}
                onDeleteCatalog={canDelete ? c => dispatch({ type: 'OPEN_DELETE_CONFIRM', payload: c }) : null}
                snackbar={state.snackbar}
                onSnackbarClose={() => dispatch({ type: 'SET_SNACKBAR', payload: null })}
                canCreate={canCreate}
              />
            </Box>

            {/* Column 2: Product List */}
            <Box sx={{ flex: 1, minWidth: 0, width: { xs: '100%' }, display: isMobile && (!state.activeCatalogId || selectedProduct) ? 'none' : 'block' }}>
              <CatalogDetailPanel
                catalog={activeCatalog}
                onBack={isMobile ? () => dispatch({ type: 'SET_ACTIVE_CATALOG', payload: null }) : null}
                onEditCatalog={canEdit ? c => dispatch({ type: 'OPEN_EDIT_DIALOG', payload: c }) : null}
                onAddFromGallery={goToGalleryForCatalog}
                onItemCountChanged={handleItemCountChanged}
                onSnackbar={msg => dispatch({ type: 'SET_SNACKBAR', payload: msg })}
                onCatalogPreviewsUpdated={(id, previewItems) =>
                  dispatch({
                    type: 'UPDATE_CATALOG_PREVIEWS',
                    payload: { id, preview_items: previewItems },
                  })
                }
                selectedProduct={selectedProduct}
                onProductSelect={setSelectedProduct}
                onItemListPatchReady={(fn) => { patchItemInListRef.current = fn; }}
                canShare={canShare}
                canDelete={canDelete}
              />
            </Box>

            {/* Column 3: Product Details (shown when a product is selected) */}
            {selectedProduct && activeCatalog && (
              <Box sx={{ width: isMobile ? '100%' : 420, flexShrink: 0, display: isMobile && !selectedProduct ? 'none' : 'block' }}>
                <ProductDetailsPanel
                  product={selectedProduct}
                  catalogId={activeCatalog.id}
                  onClose={() => setSelectedProduct(null)}
                  onUpdated={(updated) => {
                    setSelectedProduct((prev) => {
                      const merged = { ...prev, ...updated };
                      patchItemInListRef.current?.(merged);
                      return merged;
                    });
                  }}
                  onSnackbar={msg => dispatch({ type: 'SET_SNACKBAR', payload: msg })}
                />
              </Box>
            )}
          </Box>
        </Paper>
      </Box>

      <CreateEditCatalogDialog
        open={state.createDialogOpen}
        catalog={state.editCatalog}
        onClose={() => dispatch({ type: 'CLOSE_DIALOG' })}
        onUpdated={cat => {
          dispatch({ type: 'UPDATE_CATALOG', payload: cat });
          dispatch({ type: 'SET_SNACKBAR', payload: { message: 'Catalog updated!', severity: 'success' } });
        }}
        onDeleteClick={canDelete ? c => dispatch({ type: 'OPEN_DELETE_CONFIRM', payload: c }) : null}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={state.deleteConfirmOpen}
        onClose={() => dispatch({ type: 'CLOSE_DELETE_CONFIRM' })}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '16px', p: 1 } }}
      >
        <Box sx={{ px: 3, pt: 3, pb: 1 }}>
          <Typography sx={{
            fontFamily: 'var(--font-montserrat-semibold, Montserrat), sans-serif',
            fontWeight: 700,
            fontSize: '18px',
            color: '#191C1D',
            mb: 1,
          }}>
            Delete Catalog?
          </Typography>
          <Typography sx={{ fontSize: '14px', color: '#6A7282', lineHeight: 1.6 }}>
            Are you sure you want to delete{' '}
            <strong style={{ color: '#191C1D' }}>{state.catalogToDelete?.name}</strong>?
            {' '}This action cannot be undone.
          </Typography>
        </Box>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => dispatch({ type: 'CLOSE_DELETE_CONFIRM' })}
            sx={{
              textTransform: 'none',
              color: '#6A7282',
              fontFamily: 'var(--font-montserrat-medium, Montserrat), sans-serif',
              fontSize: '14px',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteCatalog}
            sx={{
              textTransform: 'none',
              bgcolor: '#E53935',
              color: '#fff',
              borderRadius: '8px',
              fontFamily: 'var(--font-montserrat-semibold, Montserrat), sans-serif',
              fontWeight: 600,
              fontSize: '14px',
              px: 3,
              '&:hover': { bgcolor: '#C62828' },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
